import { useEffect } from 'react';

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useNavigate, Link } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useState, useCallback } from 'react';

import axios from 'axios';
import { useUserStore } from './useUserStore';
import { useAppData } from "./useAppData";
import { useAppStore } from "./useAppStore";
import { useTranslation, Trans } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext'; 

import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';

export const useUserData = ({
    isVisible = true,
    onSuccessUIHandlers = {},
    onErrorUIHandlers = {},
  } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(); // Hook to access translation
  const { showNotification } = useNotification();    

  const { 
      wordList, 
      isWordListPending,
      isWordListError, 
      wordListRefetch,
      updateWordList,
      updateWordListOnReorder,
      addGroup,
      deleteGroup,
      addWordItem,
      deleteWord,
      soundboard,
      shapes,
      isSoundboardPending,
      isSoundboardError, 
      soundboardRefetch,
      addShape,
      updateShape,
      updateButton,
      unassignButton,
      deleteShape,
      handleOptionChange,
      handleAssignedWordTooltipVisibility,
      assignedWordTooltipVisibility
    } = useAppData();
      
    const { 
        baseURL,
        wordListId, setWordListId, 
        soundboardId, setSoundboardId,
        stageWidth, setStageWidth,
        stageHeight, setStageHeight,
        buttonPopovers, setButtonPopovers,
        shapePopovers, setShapePopovers,
        selectedItem, setSelectedItem,
        wordListWidth, setWordListWidth,
        lastUsedShapeColor, setLastUsedShapeColor,
        lastUsedButtonColor, setLastUsedButtonColor
    } = useAppStore();
  

    const {  
        user, 
        setUser, 
        userToken, 
        setUserToken,
        isSessionTimedOut,
        setIsSessionTimedOut,
        logout 
    } = useUserStore();


    const registerUser = useMutation({
        mutationFn: async ({ name, email, password, website, consentTracking }) => {
            return axios.post(`${baseURL}/api/users/register`, 
            { name, email, password, website, consentTracking }
            ).then((res) => res.data);
        },
        onSuccess: (data, { email }) => {
            if (data.accessToken) {
              console.log('register user', data.user)
            setUser(data.user);
            setUserToken(data.accessToken);
            setWordListId(data.wordListId);
            setSoundboardId(data.soundboardId);

            localStorage.setItem('userToken', data.accessToken);
            window.location.href = `/verify-email?email=${encodeURIComponent(email)}`;
            }
        },
        onError: (error, _, context) => {
            return error.response?.data?.message || 'Unexpected registration error';
        }
    });
    
    const loginUser = useMutation({
        mutationFn: async ({ email, password }) => {
            const response = await axios.post(`${baseURL}/api/users/login`, { email, password });

            if (!response.data || !response.data.accessToken) throw new Error('Invalid response');
            return response.data;
        },
        onSuccess: (data) => {
        const { user, accessToken, refreshToken, wordListId, soundboardId, lastUsedShapeColor, lastUsedButtonColor } = data;
        if (!user.isVerified) {
            window.location.href = `/verify-email?email=${encodeURIComponent(user.email)}`;
            return;
        }

        localStorage.setItem('userToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        setUser(user);
        setUserToken(accessToken);
        setWordListId(wordListId);
        setSoundboardId(soundboardId);
        setLastUsedShapeColor(lastUsedShapeColor);
        setLastUsedButtonColor(lastUsedButtonColor);

        queryClient.invalidateQueries(['wordList', wordListId]);
        queryClient.invalidateQueries(['soundboard', soundboardId]);

        // Consent tracking setup
        let consentTracking = false;
        if (window.klaro && typeof window.klaro.getManager === 'function') {
            const manager = window.klaro.getManager();
            consentTracking = manager.getConsent('brevo') || false;
        }

        if (consentTracking) {
            LogRocket.identify(user.id, { email: user.email, loginMethod: 'Form' });
            Sentry.setUser({ id: user.id, email: user.email });
        }

        //window.location.href = '/MainContent';
        },
    });

    /********** ACCOUNT MANAGEMENT **********/
    const fetchUserData = async () => {

      if (!userToken) {
          throw new Error(t('errors.snackbar.UserTokenNotFound'));
        }
      
        const response = await axios.get(`${baseURL}/api/users/me`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });

        setUser(response.data)
        return response.data;
      };

      const userQuery = useQuery({
        queryKey: ['user'],
        queryFn: fetchUserData,
        enabled: !!userToken,
        //enabled: !!userToken && isVisible,
        onSuccess: (data) => { 
          console.log('user fetched from backedn:', data);
          onSuccessUIHandlers?.onUserFetchSuccess?.(data);
        },
        onError: (error) => {
          const isTokenError = error.message === t('errors.snackbar.UserTokenNotFound');
      
          showNotification({
            notification: isTokenError
              ? t('errors.snackbar.UserTokenNotFound')
              : t('errors.snackbar.UserDataFetchFailed'),
            type: 'error',
            extra: isTokenError ? {} : { message: 'Failed to fetch user data' },
            error: isTokenError ? undefined : error,
            isException: false,
          });
      
          onErrorUIHandlers?.onUserFetchError?.();
        },
      });

      const updateAccount = useMutation({
        mutationFn: async ({ _id, name, email }) => {
          return axios.patch(`${baseURL}/api/users/user/${_id}/updateAccount`, {
            name,
            email
          }, {
            headers: { Authorization: `Bearer ${userToken}` }
          });
        },
        onMutate: async (newData) => {
          await queryClient.cancelQueries(['user']);
          const previousUserData = queryClient.getQueryData(['user']);
          queryClient.setQueryData(['user'], (old) => ({ ...old, ...newData }));
          return { previousUserData };
        },
        onError: (_error, _newData, context) => {
          if (context?.previousUserData) {
            queryClient.setQueryData(['user'], context.previousUserData);
          }
        },
        onSettled: () => {
          queryClient.invalidateQueries(['user']);
        }
      });

      const updatePassword = useMutation({
        mutationFn: async ({ _id, currentPassword, newPassword }) => {
          return axios.patch(`${baseURL}/api/users/user/${_id}/updatePassword`, {
            currentPassword,
            newPassword,
          }, {
            headers: { Authorization: `Bearer ${userToken}` },
          });
        },
      });

    /********** TOKEN VALIDATIOIN **********/
    // Refresh Token
    const refreshToken = useMutation({
        mutationFn: async () => {
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) throw new Error("No refresh token");
      
          const response = await axios.post(`${baseURL}/api/users/refreshToken`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
      
          return response.data.accessToken;
        },
        onSuccess: async (newAccessToken) => {
            setUserToken(newAccessToken);
            localStorage.setItem('userToken', newAccessToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        
            // ✅ Re-check session to reset idle timer etc.
            try {
            await checkSession.mutateAsync(); // call the mutation directly
            } catch (err) {
                logout();
                setIsSessionTimedOut(true);
            }
        },
        onError: (error) => {
            logout();
            setIsSessionTimedOut(true);
        },
    });

    const handleOnIdle = useCallback(() => {
        logout();
        setIsSessionTimedOut(true);
    }, []);

    const { reset } = useIdleTimer({
        timeout: 1000 * 60 * 30, // 30 minutes
        onIdle: handleOnIdle,
        debounce: 500
    });

    const checkSession = useMutation({
        mutationFn: async () => {
          const response = await axios.get(`${baseURL}/api/users/checkSession`, {
            headers: { Authorization: `Bearer ${userToken}` },
          });
          return response.status === 200;
        },
        onSuccess: () => {
          reset(); // Reset idle timer
        },
        onError: async (error) => {
          console.error("Session check failed:", error);
      
          if (error?.response?.status === 401 || error?.response?.status === 403) {
            // Attempt refresh
            try {
              await refreshToken.mutateAsync();
            } catch (refreshError) {
              console.error("Refresh failed after session check failed:", refreshError);
              logout();
              setIsSessionTimedOut(true);
            }
          } else {
            logout();
            setIsSessionTimedOut(true);
          }
        },
      });

    // Validate Token
    const validateToken = useMutation({
        mutationFn: async () => {
            const userToken = localStorage.getItem('userToken');
            if (!userToken) {
                setIsSessionTimedOut(true);
                return;
            }

            try {
                const response = await axios.post(`${baseURL}/api/users/validateToken`, 
                    { }, 
                    { headers: { Authorization: `Bearer ${userToken}` } }
                );
                
                if (response.status !== 200) {
                    setIsSessionTimedOut(true);
                    localStorage.removeItem('userToken');
                    throw new Error("Invalid token");
                }

                return response.data;
            } catch (error) {
                setIsSessionTimedOut(true);
                localStorage.removeItem('userToken');
                throw error;
            }
        }
    });

    useEffect(() => {
        const interval = setInterval(() => {
            if (userToken) {
            refreshToken.mutate();
            }
        }, 1000 * 60 * 20); // ✅ ADDED: Auto-refresh every 20 minutes
        
        return () => clearInterval(interval);
    }, [userToken]);
    

    return { 
        registerUser, 
        loginUser, 
        userQuery,
        updateAccount,
        updatePassword,
        refreshToken, 
        checkSession, 
        validateToken,
        reset
    };
};